import { useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal.jsx";
import { useRoom } from "../contexts/RoomContext.jsx";
import { GAME_CATALOG } from "../data/gameCatalog.js";
import { STOP_CATEGORIES } from "../data/stopCategories.js";

const TIME_OPTIONS = [
  [0, "Sem Tempo (Livre)"],
  [60, "1 Minuto (60s)"],
  [120, "2 Minutos (120s)"],
  [180, "3 Minutos (180s)"],
  [240, "4 Minutos (240s)"],
  [300, "5 Minutos (300s)"],
];
const ROUND_OPTIONS = [3, 5, 10];
const TERM_OPTIONS = [
  [1, "Termo (1 Palavra)"],
  [2, "Dueto (2 Palavras)"],
  [4, "Quarteto (4 Palavras)"],
];
const PUZZLE_OPTIONS = [
  [3, "Fácil (3x3 - 9 Peças)"],
  [4, "Médio (4x4 - 16 Peças)"],
  [5, "Difícil (5x5 - 25 Peças)"],
  [6, "Mestre (6x6 - 36 Peças)"],
  [7, "Lendário (7x7 - 49 Peças)"],
  [8, "Impossível (8x8 - 64 Peças)"],
];

function RoomCode({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <>
      <button
        className={`lobby-code ${copied ? "copied" : ""}`}
        onClick={copy}
        title="Clique para copiar!"
        type="button"
      >
        {copied ? "COPIADO!" : code}
      </button>
      <p className="subtitle mb-40">
        Clique no código acima para copiar e envie aos seus amigos!
      </p>
    </>
  );
}

function GameSelector({ played, isHost, onSelect }) {
  const playedIds = Object.keys(played || {});
  return (
    <div className="card lobby-card">
      <h3 className="section-title">🎮 Escolha o Jogo</h3>
      {isHost ? (
        <>
          {playedIds.length > 0 && (
            <p className="lobby-help">
              Já jogaram nesta sessão:{" "}
              {playedIds
                .map(
                  (id) =>
                    GAME_CATALOG.find((game) => game.id === id)?.nome || id,
                )
                .join(", ")}
            </p>
          )}
          <div className="game-grid">
            {GAME_CATALOG.filter((game) => !game.emBreve).map((game) => (
              <button
                className="game-card lobby-game-card"
                onClick={() => onSelect(game.id)}
                type="button"
                key={game.id}
              >
                <div className="game-img-container">
                  <img src={game.imagem} alt={game.nome} className="game-img" />
                </div>
                <div className="game-info">
                  <h3 className="game-title">
                    {game.nome} {played?.[game.id] && <span>✅ já jogado</span>}
                  </h3>
                  <p className="game-desc">{game.descricao}</p>
                </div>
                <div className="game-action">Selecionar</div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="info-box text-center">
          Aguardando o Host escolher qual jogo vocês vão jogar...
        </p>
      )}
    </div>
  );
}

function ConfigField({ label, value, isHost, options, onChange, display }) {
  return (
    <div className="lobby-config-field">
      <label>{label}</label>
      {isHost ? (
        <select
          className="select-field"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map(([optionValue, optionLabel]) => (
            <option value={optionValue} key={optionValue}>
              {optionLabel}
            </option>
          ))}
        </select>
      ) : (
        <div className="input-field lobby-readonly">{display}</div>
      )}
    </div>
  );
}

function GameConfiguration({ roomData, isHost, updateConfig, requestSwitch }) {
  const game = GAME_CATALOG.find((item) => item.id === roomData.gameId);
  const config = roomData.config || {};
  const time = config.tempoRodada ?? 60;
  const rounds = config.maxRodadas || 3;
  const termMode = config.modoTermo || 1;
  const puzzleMode = config.modoQuebraCabeca || 3;
  const imageMode = config.modoImagemQC || "aleatoria";
  return (
    <div className="card lobby-card">
      <div className="lobby-config-heading">
        <h3 className="section-title">⚙️ {game?.nome || "Configurações"}</h3>
        {isHost && (
          <button onClick={requestSwitch} type="button">
            🔀 Trocar de Jogo
          </button>
        )}
      </div>
      <div className="lobby-config-grid">
        <ConfigField
          label="Tempo por Rodada:"
          value={time}
          isHost={isHost}
          options={TIME_OPTIONS}
          onChange={(value) => updateConfig("tempoRodada", Number(value))}
          display={time === 0 ? "Sem Tempo" : `${time} segundos`}
        />
        <ConfigField
          label="Total de Rodadas:"
          value={rounds}
          isHost={isHost}
          options={ROUND_OPTIONS.map((value) => [value, `${value} rodadas`])}
          onChange={(value) => updateConfig("maxRodadas", Number(value))}
          display={`${rounds} rodadas`}
        />
        {roomData.gameId === "termo" && (
          <ConfigField
            label="Modo de Jogo:"
            value={termMode}
            isHost={isHost}
            options={TERM_OPTIONS}
            onChange={(value) => updateConfig("modoTermo", Number(value))}
            display={
              TERM_OPTIONS.find(([value]) => value === Number(termMode))?.[1]
            }
          />
        )}
        {roomData.gameId === "quebra-cabeca" && (
          <>
            <ConfigField
              label="Tamanho do Quebra-Cabeça:"
              value={puzzleMode}
              isHost={isHost}
              options={PUZZLE_OPTIONS}
              onChange={(value) =>
                updateConfig("modoQuebraCabeca", Number(value))
              }
              display={`${puzzleMode}x${puzzleMode}`}
            />
            <ConfigField
              label="Seleção de Imagem:"
              value={imageMode}
              isHost={isHost}
              options={[
                ["aleatoria", "🎲 Aleatória (sorteada)"],
                ["escolher", "🖼️ Host escolhe a cada rodada"],
              ]}
              onChange={(value) => updateConfig("modoImagemQC", value)}
              display={
                imageMode === "escolher"
                  ? "Host escolhe a cada rodada"
                  : "Aleatória (sorteada)"
              }
            />
          </>
        )}
        {roomData.gameId === "stop" && (
          <div className="lobby-config-field lobby-categories">
            <label>Categorias desta partida:</label>
            <div>
              {STOP_CATEGORIES.map((category) => (
                <span key={category.id}>
                  {category.emoji} {category.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerList({ players, currentUid, isHost, onKick }) {
  return (
    <div className="card lobby-card">
      <h3 className="section-title">
        👤 Jogadores Conectados ({players.length}/8)
      </h3>
      <ul className="player-list">
        {players.map((player) => (
          <li
            className={`player-item ${player.isHost ? "host" : ""}`}
            key={player.id}
          >
            <span>
              {player.username} {player.isHost && "👑"}
            </span>
            <div className="lobby-player-actions">
              <span className="player-points">{player.points || 0} pts</span>
              {isHost && player.id !== currentUid && (
                <button
                  className="lobby-kick"
                  onClick={() => onKick(player.id)}
                  title={`Expulsar ${player.username}`}
                  type="button"
                >
                  ✖
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LobbyPage() {
  const room = useRoom();
  const [exitOpen, setExitOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);
  if (!room.roomData?.players) {
    return <p className="text-center mt-20">Carregando dados da sala...</p>;
  }

  const players = Object.values(room.roomData.players);
  const playedCount = Object.keys(room.roomData.jogosJogados || {}).length;
  const canStart = players.length >= 2;
  return (
    <div className="container-sm text-center lobby-page">
      <h2 className="title">Sala</h2>
      <RoomCode code={room.roomCode} />

      {room.isHost && playedCount > 0 && (
        <button
          className="btn lobby-final-ranking"
          onClick={() => setRankingOpen(true)}
          type="button"
        >
          🏆 Encerrar Sessão e Ver Ranking Final
        </button>
      )}

      {!room.roomData.gameId ? (
        <GameSelector
          played={room.roomData.jogosJogados}
          isHost={room.isHost}
          onSelect={room.selecionarJogo}
        />
      ) : (
        <GameConfiguration
          roomData={room.roomData}
          isHost={room.isHost}
          updateConfig={room.updateRoomConfig}
          requestSwitch={() => setSwitchOpen(true)}
        />
      )}

      <PlayerList
        players={players}
        currentUid={room.user?.uid}
        isHost={room.isHost}
        onKick={room.kickPlayer}
      />

      <div className="mt-40">
        {room.roomData.gameId &&
          (room.isHost ? (
            <>
              <button
                className={`btn ${canStart ? "btn-success" : "btn-disabled"}`}
                onClick={room.startGame}
                disabled={!canStart}
                type="button"
              >
                Iniciar Jogo
              </button>
              {!canStart && (
                <p className="lobby-warning">
                  ⚠️ É necessário pelo menos 2 jogadores para iniciar.
                </p>
              )}
            </>
          ) : (
            <p className="info-box text-center">
              Aguardando o Host configurar e iniciar a partida...
            </p>
          ))}
        <button
          className="lobby-leave"
          onClick={() => setExitOpen(true)}
          type="button"
        >
          {room.isHost ? "Encerrar Sala" : "Sair da Sala"}
        </button>
      </div>

      <ConfirmModal
        isOpen={exitOpen}
        title={room.isHost ? "Encerrar a sala?" : "Sair da sala?"}
        message={
          room.isHost
            ? "Isso vai fechar a sala para todos os jogadores agora. Essa ação não pode ser desfeita."
            : "Você vai sair desta sala e perder sua vaga na partida."
        }
        confirmText={room.isHost ? "Sim, encerrar" : "Sim, sair"}
        onConfirm={() => {
          setExitOpen(false);
          room.leaveRoom();
        }}
        onCancel={() => setExitOpen(false)}
      />
      <ConfirmModal
        isOpen={switchOpen}
        title="Trocar de jogo?"
        message="As configurações e a pontuação de todos serão mantidas."
        confirmText="Sim, trocar"
        danger={false}
        onConfirm={() => {
          setSwitchOpen(false);
          room.trocarJogo();
        }}
        onCancel={() => setSwitchOpen(false)}
      />
      <ConfirmModal
        isOpen={rankingOpen}
        title="Encerrar a sessão de jogos?"
        message="Todos irão para o Ranking Final com o desempenho em cada jogo."
        confirmText="Sim, ver ranking"
        danger={false}
        onConfirm={() => {
          setRankingOpen(false);
          room.endGame();
        }}
        onCancel={() => setRankingOpen(false)}
      />
    </div>
  );
}
